import { Editor, Element } from 'slate'
import type { MouseEvent, ReactElement } from 'react'
import { useContext } from 'react'
import { useSlateStatic } from 'slate-react'
import cc from 'classcat'

import { ToolbarContext } from '../RichTextEditor'
import { isAnyButtonSelected, isButtonFocused, unbutton } from '../buttons'
import { isAnyLinkSelected, isLinkFocused } from '../links'
import { isAnyListSelected } from '../lists'

type Props = {
  title: string
  format: 'button' | 'unbutton'
}

export default function ButtonLinkElementButton({ title, format }: Readonly<Props>): ReactElement {
  const editor = useSlateStatic()
  const [{ isButtonLinkTooltipOpen: isOpen, isColorPickerOpen, isLinkTooltipOpen }, toolbarDispatch] =
    useContext(ToolbarContext)

  const isLinkActive = isLinkFocused(editor) || isLinkTooltipOpen
  const hasAnyLinks = isAnyLinkSelected(editor)
  const isActive = isButtonFocused(editor) || isOpen
  const hasAnyButtons = isAnyButtonSelected(editor)
  const multipleParagraphsSelected =
    Array.from(Editor.nodes(editor, { match: (n) => Element.isElement(n) && n.type === 'paragraph' })).length > 1
  const hasAnyLists = isAnyListSelected(editor)

  function handleMouseDown(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault()

    if (format === 'button') {
      toolbarDispatch({ type: 'open_button_tooltip' })
    } else if (format === 'unbutton') {
      unbutton(editor)
      toolbarDispatch({ type: 'close_button_tooltip' })
    }
  }

  return (
    <button
      className={cc([
        'dali-plugin-text-toolbar-button',
        `dali-plugin-text-toolbar-button-${format}`,
        { active: isActive && format === 'button' },
      ])}
      data-command-name={format}
      type="button"
      title={title}
      onMouseDown={handleMouseDown}
      disabled={
        (!editor.selection && !isOpen && !isColorPickerOpen) ||
        isLinkActive ||
        (format === 'unbutton' && !hasAnyButtons) ||
        (format === 'button' &&
          !isActive &&
          (hasAnyButtons || hasAnyLinks || multipleParagraphsSelected || hasAnyLists))
      }
    />
  )
}
